import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { purchaseAPI, adminAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Plus, X, Check, FileText, TrendingUp, Download, Upload, Search } from "lucide-react";
import { fmtDate } from "../utils/dateUtils";
import { exportCSV, parseCSV } from "../utils/csvUtils";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ImportCSVButton({ onImport }) {
  const ref = useRef();
  return (
    <>
      <button onClick={() => ref.current.click()} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Upload size={14} /> Import</button>
      <input ref={ref} type="file" accept=".csv" className="hidden" onChange={e => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = ev => onImport(parseCSV(ev.target.result)); reader.readAsText(file); e.target.value = ''; }} />
    </>
  );
}

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  ordered: "bg-blue-100 text-blue-700",
  received: "bg-green-100 text-green-700",
  fulfilled: "bg-[#EDF2EE] text-[#637C64]",
};

export default function Purchase() {
  const { kitchens: ctxKitchens, selectedKitchen: globalKitchen } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);

  // Keep in sync with context + direct fallback fetch if empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const t = setTimeout(() => {
      if (kitchens.length > 0) return;
      usersAPI.getKitchens().then(r => {
        const ks = r.data || [];
        if (ks.length > 0) setKitchens(ks);
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [ctxKitchens]);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "indents");
  useEffect(() => { const t = searchParams.get("tab"); if (t) setActiveTab(t); }, [searchParams]);
  const [indents, setIndents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quoteAnalysis, setQuoteAnalysis] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ quotes: [] });
  const [poItems, setPoItems] = useState([{ item_id: "", quantity: "", rate: "" }]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  // Sync local kitchen filter with global kitchen context
  const [filterKitchen, setFilterKitchen] = useState(globalKitchen || "");
  useEffect(() => { setFilterKitchen(globalKitchen || ""); }, [globalKitchen]);

  useEffect(() => { fetchBase(); }, []);
  useEffect(() => { fetchTab(); }, [activeTab]);

  const fetchBase = async () => {
    const items = await adminAPI.getMasterItems();
    setMasterItems(items.data || []);
  };

  const fetchTab = async () => {
    setLoading(true);
    try {
      if (activeTab === "indents") {
        const r = await purchaseAPI.getIndents();
        setIndents(r.data || []);
      } else if (activeTab === "orders") {
        const r = await purchaseAPI.getOrders();
        setOrders(r.data || []);
      } else if (activeTab === "quotes") {
        const r = await purchaseAPI.getQuoteAnalysis();
        setQuoteAnalysis(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleCreatePO = async () => {
    setError("");
    const validItems = poItems.filter(i => i.item_id && parseFloat(i.quantity) > 0 && parseFloat(i.rate) > 0);
    if (validItems.length === 0) { setError("Add at least one item with quantity and rate"); return; }
    if (!form.vendor_name) { setError("Vendor name is required"); return; }
    if (!form.kitchen_id) { setError("Kitchen is required"); return; }
    try {
      // Create one PO per item row (same vendor, kitchen, notes)
      for (const item of validItems) {
        await purchaseAPI.createOrder({
          item_id: item.item_id,
          quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate),
          kitchen_id: form.kitchen_id,
          vendor_name: form.vendor_name,
          quotes: form.quotes || [],
          notes: form.notes || "",
          indent_ids: form.indent_ids || [],
        });
      }
      setModal(null);
      setForm({ quotes: [] });
      setPoItems([{ item_id: "", quantity: "", rate: "" }]);
      fetchTab();
    } catch (e) { setError(formatError(e)); }
  };

  const handleApproveIndent = (indent) => {
    // Open PO creation modal pre-filled with indent data
    setForm({
      quotes: [],
      kitchen_id: indent.kitchen_id || globalKitchen || "",
      notes: `From indent: ${indent.item_name}`,
      indent_ids: [indent.id],
    });
    setPoItems([{ item_id: indent.item_id, quantity: indent.quantity_required, rate: "" }]);
    setModal("create-po");
    setError("");
  };

  const handleRejectIndent = async (indent) => {
    try {
      await purchaseAPI.updateIndentStatus(indent.id, "rejected");
      fetchTab();
    } catch (e) { alert(formatError(e)); }
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await purchaseAPI.updateOrderStatus(orderId, status);
      fetchTab();
    } catch (e) { alert(formatError(e)); }
  };

  const addQuote = () => setForm(f => ({ ...f, quotes: [...(f.quotes || []), { vendor_name: "", rate: 0 }] }));
  const updateQuote = (i, field, val) => setForm(f => { const q = [...f.quotes]; q[i] = { ...q[i], [field]: val }; return { ...f, quotes: q }; });

  // Filtered data
  const filteredIndents = indents.filter(i =>
    (!search || i.item_name?.toLowerCase().includes(search.toLowerCase()) || i.raised_by_name?.toLowerCase().includes(search.toLowerCase())) &&
    (!filterStatus || i.status === filterStatus) && (!filterKitchen || i.kitchen_id === filterKitchen)
  );
  const filteredOrders = orders.filter(o =>
    (!search || o.item_name?.toLowerCase().includes(search.toLowerCase()) || o.vendor_name?.toLowerCase().includes(search.toLowerCase()) || o.po_number?.toLowerCase().includes(search.toLowerCase())) &&
    (!filterStatus || o.status === filterStatus) && (!filterKitchen || o.kitchen_id === filterKitchen)
  );
  const filteredQuotes = quoteAnalysis.filter(q => !search || q.item_name?.toLowerCase().includes(search.toLowerCase()) || q.vendor_name?.toLowerCase().includes(search.toLowerCase()));

  const TABS = [
    { id: "indents", label: "Indents Received", icon: FileText },
    { id: "orders", label: "Purchase Orders", icon: Check },
    { id: "quotes", label: "Quote Analysis", icon: TrendingUp },
  ];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="purchase-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Purchase Module</h1>
        {activeTab === "orders" && (
          <button onClick={() => {
            setForm({ quotes: [], kitchen_id: globalKitchen || "" });
            setPoItems([{ item_id: "", quantity: "", rate: "" }]);
            setModal("create-po");
            setError("");
          }}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1">
            <Plus size={14} /> Create PO
          </button>
        )}
      </div>

      <div className="flex border-b border-[#E5E1DB]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {/* Search + Filter + Export bar */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex-1 relative min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
          <input placeholder="Search..." className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {activeTab !== "quotes" && (
          <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            {["pending","ordered","received","fulfilled"].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
        )}
        <select
          className={`border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] ${filterKitchen ? "border-[#E8956D] bg-[#E8956D]/5 font-semibold text-[#E8956D]" : "border-[#E5E1DB]"}`}
          value={filterKitchen}
          onChange={e => setFilterKitchen(e.target.value)}
          data-testid="purchase-kitchen-filter"
        >
          <option value="">All Kitchens</option>
          {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        <button
          onClick={() => {
            if (activeTab === "indents") exportCSV(filteredIndents.map(i => ({ date: i.created_at, item_name: i.item_name, qty: i.quantity_required, kitchen: i.kitchen_name, raised_by: i.raised_by_name, status: i.status })), "indents");
            else if (activeTab === "orders") exportCSV(filteredOrders.map(o => ({ po_number: o.po_number, item: o.item_name, qty: o.quantity, unit: o.unit, vendor: o.vendor_name, rate: o.rate, total: o.total_amount, status: o.status })), "purchase_orders");
            else exportCSV(filteredQuotes.map(q => ({ item: q.item_name, vendor: q.vendor_name, avg_rate: q.avg_rate, min_rate: q.min_rate, max_rate: q.max_rate, orders: q.order_count })), "quote_analysis");
          }}
          className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
          <Download size={14} /> Export
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* Indents */}
          {activeTab === "indents" && (
            <div className="space-y-3">
              {Object.entries(
                filteredIndents.reduce((groups, indent) => {
                  const key = indent.kitchen_name || (indent.kitchen_id ? indent.kitchen_id : "General / All Kitchens");
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(indent);
                  return groups;
                }, {})
              ).map(([kitchen, kitchenIndents]) => (
                <div key={kitchen} className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                    <h3 className="font-bold text-[#3A322C] text-sm">{kitchen}</h3>
                  </div>
                  <div className="divide-y divide-[#F4F3F0]">
                    {kitchenIndents.map(indent => (
                      <div key={indent.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#3A322C] text-sm">{indent.item_name}</p>
                          <p className="text-xs text-[#8A7E73]">Required: {indent.quantity_required} | By: {indent.raised_by_name || "—"} | {fmtDate(indent.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[indent.status] || "bg-gray-100 text-gray-700"}`}>{indent.status}</span>
                          {indent.status === "pending" && (
                            <>
                              <button
                                onClick={() => handleApproveIndent(indent)}
                                className="text-xs bg-[#637C64] text-white px-3 py-1.5 rounded-lg font-bold hover:bg-[#4F6350] flex items-center gap-1"
                                data-testid={`approve-indent-${indent.id}`}
                              >
                                ✓ Create PO
                              </button>
                              <button
                                onClick={() => handleRejectIndent(indent)}
                                className="text-xs border border-[#C2624E] text-[#C2624E] px-3 py-1.5 rounded-lg font-bold hover:bg-[#FBEAE7]"
                                data-testid={`reject-indent-${indent.id}`}
                              >
                                ✕ Reject
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {filteredIndents.length === 0 && <div className="text-center py-16 text-[#8A7E73]">No indents found</div>}
            </div>
          )}

          {/* Purchase Orders */}
          {activeTab === "orders" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["PO #", "Item", "Qty", "Vendor", "Rate", "Total", "Kitchen", "Status", "Actions"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {filteredOrders.map(o => (
                      <tr key={o.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 font-bold text-[#E8956D]">{o.po_number}</td>
                        <td className="px-4 py-3 text-[#3A322C]">{o.item_name}</td>
                        <td className="px-4 py-3">{o.quantity} {o.unit}</td>
                        <td className="px-4 py-3 text-[#8A7E73]">{o.vendor_name}</td>
                        <td className="px-4 py-3">₹{o.rate}</td>
                        <td className="px-4 py-3 font-bold">₹{o.total_amount?.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {o.kitchen_name ? (
                            <span className="text-xs font-bold bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full whitespace-nowrap">{o.kitchen_name}</span>
                          ) : <span className="text-xs text-[#C4BAAF]">—</span>}
                          {o.created_by_name && <p className="text-[10px] text-[#8A7E73] mt-0.5">by {o.created_by_name}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-700"}`}>{o.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          {o.status === "pending" && (
                            <button onClick={() => handleUpdateStatus(o.id, "ordered")} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-bold">Mark Ordered</button>
                          )}
                          {o.status === "ordered" && (
                            <button onClick={() => handleUpdateStatus(o.id, "received")} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded font-bold">Mark Received</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredOrders.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8A7E73]">No orders found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Quote Analysis */}
          {activeTab === "quotes" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Item", "Vendor", "Avg Rate", "Min Rate", "Max Rate", "Orders"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {filteredQuotes.map((q, i) => (
                      <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 font-medium text-[#3A322C]">{q.item_name}</td>
                        <td className="px-4 py-3 text-[#5C5147]">{q.vendor_name}</td>
                        <td className="px-4 py-3 font-bold text-[#E8956D]">₹{q.avg_rate?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-[#637C64]">₹{q.min_rate?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-[#C2624E]">₹{q.max_rate?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-[#8A7E73]">{q.order_count}</td>
                      </tr>
                    ))}
                    {quoteAnalysis.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No quote data yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create PO Modal */}
      {modal === "create-po" && (
        <Modal title="Create Purchase Order" onClose={() => setModal(null)}>
          <div className="space-y-4">

            {/* Vendor + Kitchen (shared) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vendor Name *</label>
                <input
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  placeholder="e.g. Fresh Veggies Pvt Ltd"
                  value={form.vendor_name || ""}
                  onChange={e => setForm({ ...form, vendor_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen *</label>
                <select
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.kitchen_id || ""}
                  onChange={e => setForm({ ...form, kitchen_id: e.target.value })}
                >
                  <option value="">Select Kitchen</option>
                  {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              </div>
            </div>

            {/* Multi-item rows */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">
                  Items Ordered ({poItems.filter(i => i.item_id).length} item{poItems.filter(i => i.item_id).length !== 1 ? "s" : ""})
                </label>
              </div>

              {/* Header row */}
              <div className="grid grid-cols-[1fr_80px_80px_28px] gap-2 px-3 py-2 bg-[#F4F3F0] rounded-t-lg">
                <span className="text-xs font-bold uppercase text-[#5C5147]">Item</span>
                <span className="text-xs font-bold uppercase text-[#5C5147] text-center">Qty</span>
                <span className="text-xs font-bold uppercase text-[#5C5147] text-center">Rate (₹)</span>
                <span />
              </div>

              <div className="border border-[#E5E1DB] rounded-b-lg overflow-hidden divide-y divide-[#F4F3F0]">
                {poItems.map((item, i) => {
                  const masterItem = masterItems.find(m => m.id === item.item_id);
                  const lineTotal = parseFloat(item.quantity || 0) * parseFloat(item.rate || 0);
                  return (
                    <div key={`idx-${i}`} className="grid grid-cols-[1fr_80px_80px_28px] gap-2 px-3 py-2.5 items-center hover:bg-[#FAFAF8]">
                      <select
                        className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full"
                        value={item.item_id}
                        onChange={e => setPoItems(prev => prev.map((p, j) => j === i ? { ...p, item_id: e.target.value } : p))}
                      >
                        <option value="">Select item</option>
                        {masterItems.map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
                      </select>
                      <input
                        type="number" step="0.01" min="0" placeholder="0"
                        className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs text-center outline-none focus:border-[#E8956D] w-full"
                        value={item.quantity}
                        onChange={e => setPoItems(prev => prev.map((p, j) => j === i ? { ...p, quantity: e.target.value } : p))}
                      />
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8A7E73] text-[10px]">₹</span>
                        <input
                          type="number" step="0.01" min="0" placeholder="0"
                          className="border border-[#E5E1DB] rounded-lg pl-4 pr-1 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full"
                          value={item.rate}
                          onChange={e => setPoItems(prev => prev.map((p, j) => j === i ? { ...p, rate: e.target.value } : p))}
                        />
                      </div>
                      {poItems.length > 1 ? (
                        <button onClick={() => setPoItems(prev => prev.filter((_, j) => j !== i))} className="text-[#C4BAAF] hover:text-[#C2624E]">
                          <X size={14} />
                        </button>
                      ) : <span />}
                    </div>
                  );
                })}

                {/* Subtotals + Add row */}
                <div className="px-3 py-2.5 bg-[#FAFAF8] flex items-center justify-between">
                  <button
                    onClick={() => setPoItems(prev => [...prev, { item_id: "", quantity: "", rate: "" }])}
                    className="text-xs text-[#E8956D] font-bold hover:underline flex items-center gap-1"
                  >
                    <Plus size={12} /> Add Another Item
                  </button>
                  {poItems.some(i => i.quantity && i.rate) && (
                    <span className="text-xs font-bold text-[#3A322C]">
                      Total: ₹{poItems.reduce((s, i) => s + parseFloat(i.quantity || 0) * parseFloat(i.rate || 0), 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Vendor Quotes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Vendor Quotes (for comparison)</label>
                <button onClick={addQuote} className="text-xs text-[#E8956D] font-bold">+ Add Quote</button>
              </div>
              <div className="space-y-2">
                {(form.quotes || []).map((q, i) => (
                  <div key={`idx-${i}`} className="grid grid-cols-2 gap-2">
                    <input placeholder="Vendor Name" className="border border-[#E5E1DB] rounded-lg px-2 py-2 text-xs outline-none focus:border-[#E8956D]" value={q.vendor_name || ""} onChange={e => updateQuote(i, "vendor_name", e.target.value)} />
                    <input type="number" placeholder="Rate ₹" className="border border-[#E5E1DB] rounded-lg px-2 py-2 text-xs outline-none focus:border-[#E8956D]" value={q.rate || ""} onChange={e => updateQuote(i, "rate", parseFloat(e.target.value) || 0)} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleCreatePO}
                className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">
                Create {poItems.filter(i => i.item_id && i.quantity && i.rate).length || ""} PO{poItems.filter(i => i.item_id && i.quantity && i.rate).length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
